import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { CheckCircle, Video, FileText, Brain, ArrowLeft, ArrowRight, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import VideoLockdownPlayer from "../components/VideoLockdownPlayer";

function isDirectVideo(url) {
  return /\.(mp4|webm|mov|m4v|ogv)(\?|#|$)/i.test(url || "");
}

export default function LessonPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [summary, setSummary] = useState("");
  const [summarizing, setSummarizing] = useState(false);
  const [marking, setMarking] = useState(false);
  const [moduleCompleteDialog, setModuleCompleteDialog] = useState(null);

  const checkModuleCompletion = async () => {
    try {
      const { data } = await api.get(`/courses/${lesson?.course_id}/module-access`);
      // Find the module for THIS lesson
      const myModule = (course?.modules || []).find(m =>
        (m.lessons || []).some(l => l.id === id)
      );
      if (!myModule) return;
      const myModAccess = data.find(a => a.module_id === myModule.id);
      if (myModAccess?.completed && myModAccess.total_lessons > 0) {
        // Only celebrate once per module per user
        const key = `sbl_mod_celebrated_${myModule.id}`;
        if (!localStorage.getItem(key)) {
          setModuleCompleteDialog({
            title: myModule.title,
            totalLessons: myModAccess.total_lessons,
            moduleId: myModule.id,
          });
          localStorage.setItem(key, "1");
        }
      }
    } catch {}
  };

  useEffect(() => {
    api.get(`/lessons/${id}`).then(async (r) => {
      setLesson(r.data);
      if (r.data.course_id) {
        try {
          const cr = await api.get(`/courses/${r.data.course_id}`);
          setCourse(cr.data);
          const pr = await api.get(`/progress/course/${r.data.course_id}`);
          if (pr.data.completed_lesson_ids?.includes(id)) setCompleted(true);
        } catch {}
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const markComplete = async () => {
    setMarking(true);
    try {
      await api.post("/progress/complete", { lesson_id: id, course_id: lesson.course_id });
      setCompleted(true);
      toast.success("Lesson marked as complete!");
      // Check if this lesson was the last in its module → celebrate
      await checkModuleCompletion();
      // D5 — Auto-redirect to next module's quiz / next lesson
      await autoAdvance();
    } catch { toast.error("Failed to mark complete"); }
    setMarking(false);
  };

  /**
   * After marking complete, determine the "next step":
   *  1. If current module still has un-watched lessons → go to next lesson in module
   *  2. Else if current module has a quiz the user has NOT yet passed → go to quiz
   *  3. Else if there's a next module → go to its first lesson
   *  4. Else stay (course finished)
   */
  const autoAdvance = async () => {
    if (!course?.modules || !lesson) return;
    const myModule = course.modules.find(m => (m.lessons || []).some(l => l.id === id));
    if (!myModule) return;
    const myLessons = myModule.lessons || [];
    const myIdx = myLessons.findIndex(l => l.id === id);
    // 1. Next lesson in same module
    if (myIdx >= 0 && myIdx < myLessons.length - 1) {
      const next = myLessons[myIdx + 1];
      toast.info(`Up next: ${next.title}`);
      setTimeout(() => navigate(`/lessons/${next.id}`), 1200);
      return;
    }
    // 2. Module quiz?
    try {
      const { data: prog } = await api.get(`/courses/${lesson.course_id}/module-progression`);
      const myProg = (prog.modules || []).find(p => p.module_id === myModule.id);
      if (myProg?.has_quiz && myProg.quiz_id && myProg.quiz_status !== "passed") {
        toast.info("All lessons done — time for the module quiz!");
        setTimeout(() => navigate(`/quizzes/${myProg.quiz_id}`), 1500);
        return;
      }
      // 3. First lesson of next module
      const allModules = course.modules;
      const modIdx = allModules.findIndex(m => m.id === myModule.id);
      const nextModule = allModules[modIdx + 1];
      if (nextModule && nextModule.lessons?.[0]) {
        toast.info(`Module complete — moving to "${nextModule.title}"`);
        setTimeout(() => navigate(`/lessons/${nextModule.lessons[0].id}`), 1500);
      }
    } catch (e) { /* silent */ }
  };

  const generateSummary = async () => {
    setSummarizing(true);
    try {
      const { data } = await api.post("/ai/summarize", { lesson_id: id });
      setSummary(data.summary);
    } catch (e) { toast.error("Failed to generate summary"); }
    setSummarizing(false);
  };

  // Find next/prev lessons
  let prevLesson = null, nextLesson = null;
  if (course?.modules) {
    const allLessons = course.modules.flatMap(m => m.lessons || []);
    const idx = allLessons.findIndex(l => l.id === id);
    if (idx > 0) prevLesson = allLessons[idx - 1];
    if (idx < allLessons.length - 1) nextLesson = allLessons[idx + 1];
  }

  if (loading) return <Layout><div className="p-8"><div className="h-64 bg-slate-100 animate-pulse" /></div></Layout>;
  if (!lesson) return <Layout><div className="p-8 text-center"><p className="text-slate-500">Lesson not found</p></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="lesson-page">
        <Link to={lesson.course_id ? `/courses/${lesson.course_id}` : "/courses"} className="text-sm text-blue-800 hover:underline mb-4 inline-block">
          &larr; Back to Course
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {lesson.content_type === "video" ? <Video size={18} className="text-blue-800" /> : <FileText size={18} className="text-slate-500" />}
              <Badge variant="secondary" className="rounded-none text-xs capitalize">{lesson.content_type}</Badge>
              {completed && <Badge className="rounded-none text-xs bg-emerald-100 text-emerald-700">Completed</Badge>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {lesson.title}
            </h1>
          </div>
        </div>

        {/* Video Content */}
        {(lesson.content_type === "video" || lesson.content_type === "mixed") && lesson.video_url && (
          <div className="mb-6 border border-slate-200" data-testid="lesson-video-wrap">
            {isDirectVideo(lesson.video_url) ? (
              <VideoLockdownPlayer
                src={lesson.video_url}
                lessonId={id}
                // Staff (super_admin / admin / instructor) bypass first-watch
                // lockdown so they can review, scrub, and QA the video freely.
                enforce={course?.enforce_video_lockdown !== false && user?.role === "student"}
                onCompleted={() => { if (!completed && user?.role === "student") { markComplete(); } }}
              />
            ) : (
              <div className="aspect-video bg-black">
                <iframe
                  src={(() => {
                    let url = lesson.video_url;
                    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
                    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
                    return url;
                  })()}
                  title={lesson.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        )}

        {/* Text Content */}
        {(lesson.content_type === "text" || lesson.content_type === "mixed") && lesson.text_content && (
          <Card className="border border-slate-200 rounded-none shadow-sm mb-6">
            <CardContent className="p-6 md:p-8">
              <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                {lesson.text_content.split("\n").map((line, i) => {
                  if (line.startsWith("**") && line.endsWith("**")) return <h3 key={i} className="text-lg font-semibold text-slate-900 mt-4 mb-2">{line.replace(/\*\*/g, "")}</h3>;
                  if (line.startsWith("- ") || line.match(/^\d+\.\s/)) return <p key={i} className="ml-4 mb-1">{line}</p>;
                  return <p key={i} className="mb-2">{line}</p>;
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Summary */}
        <div className="flex gap-3 mb-6 flex-wrap">
          {!completed && user?.role === "student" && !lesson.require_video && (
            <Button onClick={markComplete} disabled={marking} className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-none gap-2" data-testid="mark-complete-btn">
              <CheckCircle size={16} /> {marking ? "Marking..." : "Mark as Complete"}
            </Button>
          )}
          {!completed && user?.role === "student" && lesson.require_video && (
            <div className="text-xs text-slate-500 border border-slate-200 bg-slate-50 px-3 py-2 rounded-none" data-testid="require-video-notice">
              This lesson auto-completes once you've watched at least 90% of the video.
            </div>
          )}
          {lesson.text_content && (
            <Button onClick={generateSummary} disabled={summarizing} variant="outline" className="rounded-none gap-2 border-slate-300" data-testid="ai-summary-btn">
              <Brain size={16} className="text-blue-800" /> {summarizing ? "Generating..." : "AI Summary"}
            </Button>
          )}
        </div>

        {summary && (
          <Card className="border border-blue-200 rounded-none shadow-sm mb-6 bg-blue-50/50">
            <CardContent className="p-6">
              <h3 className="text-base font-semibold text-blue-900 mb-3 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <Brain size={18} className="text-blue-800" /> AI Summary
              </h3>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{summary}</p>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <Separator className="my-6" />
        <div className="flex items-center justify-between">
          {prevLesson ? (
            <Link to={`/lessons/${prevLesson.id}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-800 transition-colors" data-testid="prev-lesson-btn">
              <ArrowLeft size={16} /> {prevLesson.title}
            </Link>
          ) : <div />}
          {nextLesson ? (
            <Link to={`/lessons/${nextLesson.id}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-800 transition-colors" data-testid="next-lesson-btn">
              {nextLesson.title} <ArrowRight size={16} />
            </Link>
          ) : <div />}
        </div>
      </div>

      {/* Module Completion Celebration */}
      <Dialog open={!!moduleCompleteDialog} onOpenChange={(o) => !o && setModuleCompleteDialog(null)}>
        <DialogContent className="rounded-none sm:max-w-md text-center p-0 overflow-hidden" data-testid="module-complete-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>Module Completed</DialogTitle>
          </DialogHeader>
          <div className="bg-gradient-to-b from-blue-700 to-blue-900 text-white py-6 px-6">
            <div className="text-4xl mb-2">&#128170; &#128079; &#127775;</div>
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Module Completed!</h2>
            <p className="text-sm text-blue-100">Great momentum — keep it going!</p>
          </div>
          <div className="p-6">
            <div className="w-16 h-16 mx-auto mb-3 bg-emerald-600 text-white rounded-full flex items-center justify-center">
              <CheckCircle size={36} weight="fill" />
            </div>
            <p className="text-sm text-slate-600 mb-1">You've finished all {moduleCompleteDialog?.totalLessons} lessons in</p>
            <p className="text-lg font-bold text-slate-950 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {moduleCompleteDialog?.title}
            </p>
            {nextLesson ? (
              <Link to={`/lessons/${nextLesson.id}`} onClick={() => setModuleCompleteDialog(null)}>
                <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none w-full gap-2" data-testid="next-module-btn">
                  Continue to Next Lesson <ArrowRight size={14} />
                </Button>
              </Link>
            ) : (
              <Button onClick={() => setModuleCompleteDialog(null)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none w-full" data-testid="close-module-celebrate">
                Great, thanks!
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
